import { ChangeDetectionStrategy, Component, effect, ElementRef, inject, input, NgZone, OnDestroy, output, signal, SimpleChanges, untracked, ViewChild, ViewEncapsulation } from '@angular/core';
import { createSearchPattern, escapeHtml } from '../../utils';

@Component({
  selector: 'text-highlight',
  template: `
    <span #spanDefault></span>
    <span #spanMatched hidden></span>
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TextHighlightComponent implements OnDestroy {
  readonly zone    = inject(NgZone);

  readonly text    = input('');
  readonly pattern = input<RegExp[] | RegExp | string>('');
  readonly matched = output<boolean>();

  destroyed  = false;
  wasMatched = undefined;

  @ViewChild('spanDefault', { static: true }) spanDefault: ElementRef<HTMLSpanElement>;
  @ViewChild('spanMatched', { static: true }) spanMatched: ElementRef<HTMLSpanElement>;

  constructor() {
    effect(() => {
      let _ = [this.text(), this.pattern()];
      untracked(() => this.applyUpdate());
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
  }

  updateDefaultSpan() {
    this.spanDefault.nativeElement.innerHTML = this.text();
  }

  applyUpdate() {
    if (this.destroyed)
      return;

    let matched = false;

    if (this.destroyed || !this.spanDefault || !this.spanMatched)
      return;

    this.updateDefaultSpan();
    
    let [text, pattern] = ['' + (this.text() ?? ''), this.pattern()];

    let html: string;
    
    let hasText    = text.trim() != '';
    let hasPattern = pattern && (pattern != '' || (Array.isArray(pattern) && pattern.length > 1));

    let regexes: RegExp[];

    if (!hasText) {
      html = '';
    }
    else if (!hasPattern) {
      html = text ? escapeHtml(text) : '';
    }
    else {
      if (typeof pattern == 'string') {
        regexes = [createSearchPattern(pattern)];
      }
      else {
        regexes = Array.isArray(pattern) ? pattern : [pattern];
      }

      let patternMatches = 0;

      html = text;

      regexes.forEach(p => {
        let anyMatches = false;

        const specialToken1 = '\u0001\u0001';
        const specialToken2 = '\u0001\u0002';
        
        html = html.replaceAll('<mark>', specialToken1);
        html = html.replaceAll('</mark>', specialToken2);

        html = html.replace(p, m => {
          anyMatches = true;
          return [specialToken1, escapeHtml(m), specialToken2].join('');
        });
        
        html = html.replaceAll(specialToken1, '<mark>');
        html = html.replaceAll(specialToken2, '</mark>');

        if (anyMatches) {
          patternMatches++;
        }
      });

      if (patternMatches === regexes.length)
        matched = true;
    }

    if ((matched || this.wasMatched) || this.wasMatched === undefined) {
      this.wasMatched = matched;
      this.matched.emit(matched);

      this.spanDefault.nativeElement.hidden = matched;
      this.spanMatched.nativeElement.hidden = !matched;

      if (matched) {
        this.spanMatched.nativeElement.innerHTML = html;
      }
    }
  }
};
